import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  reactionTypes,
  contentTypeConfigs,
  topReactedContent,
  userReactions,
  reactionDashboardStats,
  scoreActions,
  getReactionById,
  getContentTypeById,
  getReactionsForContentType,
  getContentByType,
  type ReactionId,
  type ContentTypeId,
  type ReactionType,
  type ContentItem,
} from '@/mocks/rfwReactions';

function ReactionBubble({ reaction, size = 'md', showName = true }: { reaction: ReactionType; size?: 'sm' | 'md' | 'lg'; showName?: boolean }) {
  const sizeClasses = size === 'lg' ? 'w-24 h-24 text-3xl' : size === 'md' ? 'w-18 h-18 text-2xl' : 'w-14 h-14 text-xl';
  const nameSize = size === 'lg' ? 'text-sm' : 'text-xs';
  return (
    <div className="flex flex-col items-center gap-1.5 group cursor-default">
      <div className={`${sizeClasses} rounded-full bg-gradient-to-r ${reaction.gradient} flex items-center justify-center shadow-md ${reaction.glowColor} transition-transform duration-200 group-hover:scale-110`}>
        <span>{reaction.emoji}</span>
      </div>
      {showName && <span className={`${nameSize} font-bold ${reaction.textColor} text-center leading-tight`}>{reaction.shortName}</span>}
    </div>
  );
}

function ReactionDetailCard({ reaction, expanded, onToggle }: { reaction: ReactionType; expanded: boolean; onToggle: () => void }) {
  return (
    <div
      onClick={onToggle}
      className={`rounded-2xl border cursor-pointer transition-all duration-300 ${
        expanded
          ? `${reaction.bgColor} ${reaction.borderColor} shadow-sm col-span-1 sm:col-span-2 lg:col-span-3`
          : 'bg-background-50 border-background-200/70 hover:border-foreground-300/50'
      }`}
    >
      <div className="p-5">
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${reaction.gradient} flex items-center justify-center shadow-md ${reaction.glowColor} shrink-0`}>
            <span className="text-2xl">{reaction.emoji}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className={`text-lg font-bold ${reaction.textColor}`}>{reaction.name}</h4>
            <p className="text-xs text-foreground-500 mt-0.5">{reaction.description}</p>
            <div className="flex items-center gap-3 mt-1.5">
              <span className="text-sm font-bold text-foreground-950">{reaction.totalCount.toLocaleString()}</span>
              <span className={`text-[10px] font-semibold ${reaction.trend === 'up' ? 'text-emerald-600' : reaction.trend === 'down' ? 'text-red-500' : 'text-foreground-400'}`}>
                {reaction.trend === 'up' ? '+' : reaction.trend === 'down' ? '-' : ''}{reaction.trendPercent}%
              </span>
              <span className="text-[10px] text-foreground-400">Rank #{reaction.rank}</span>
              <span className="text-[10px] text-amber-600 font-semibold">+{reaction.scoreValue} pts</span>
            </div>
          </div>
          <div className="text-right shrink-0">
            <span className="text-[10px] text-foreground-400">Score</span>
            <p className={`text-lg font-bold ${reaction.textColor}`}>+{reaction.scoreValue}</p>
          </div>
        </div>
        {expanded && (
          <div className="mt-4 pt-4 border-t border-foreground-200/30">
            <p className="text-sm text-foreground-700 leading-relaxed mb-3">{reaction.description}</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-xl bg-white/50 border border-white/30">
                <p className="text-[9px] text-foreground-500">TOTAL</p>
                <p className="text-lg font-bold text-foreground-950">{reaction.totalCount.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-xl bg-white/50 border border-white/30">
                <p className="text-[9px] text-foreground-500">TENDENCIA</p>
                <p className={`text-lg font-bold ${reaction.trend === 'up' ? 'text-emerald-600' : 'text-red-500'}`}>
                  {reaction.trend === 'up' ? '+' : '-'}{reaction.trendPercent}%
                </p>
              </div>
              <div className="p-3 rounded-xl bg-white/50 border border-white/30">
                <p className="text-[9px] text-foreground-500">SCORE</p>
                <p className="text-lg font-bold text-amber-600">+{reaction.scoreValue} pts</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ContentListCard({ item }: { item: ContentItem }) {
  const mainReaction = getReactionById(item.mainReaction);
  const typeInfo = getContentTypeById(item.type);
  return (
    <div className="flex items-center gap-4 p-4 rounded-2xl bg-background-50 border border-background-200/70 hover:border-foreground-300/50 transition-all duration-200">
      <div className="w-20 h-20 rounded-xl overflow-hidden shrink-0">
        <img src={item.thumbnailUrl} alt={item.title} className="w-full h-full object-cover object-top" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {typeInfo && (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-semibold ${typeInfo.bgColor} ${typeInfo.textColor}`}>
              <i className={`${typeInfo.icon} text-[7px]`}></i>
              {typeInfo.name}
            </span>
          )}
          {item.trending && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-orange-100 text-orange-600">
              <i className="ri-fire-line text-[7px]"></i>TREND
            </span>
          )}
        </div>
        <p className="text-sm font-semibold text-foreground-950 line-clamp-2 leading-snug mb-1">{item.title}</p>
        <div className="flex items-center gap-2 text-[10px] text-foreground-500">
          <span>{item.radio}</span>
          <span>·</span>
          <span>{item.timeAgo}</span>
        </div>
      </div>
      <div className="flex flex-col items-end shrink-0 gap-1">
        <div className="flex items-center gap-1.5">
          {mainReaction && <span className="text-base">{mainReaction.emoji}</span>}
          <span className="text-lg font-bold text-foreground-950">{item.totalReactions.toLocaleString()}</span>
        </div>
        {mainReaction && (
          <span className={`text-[9px] font-semibold ${mainReaction.textColor}`}>
            {mainReaction.name} {item.mainReactionPct}%
          </span>
        )}
      </div>
    </div>
  );
}

export default function ReaccionesPage() {
  const [view, setView] = useState<'galeria' | 'contenido' | 'metricas' | 'score'>('galeria');
  const [expandedReaction, setExpandedReaction] = useState<ReactionId | null>(null);
  const [selectedContentType, setSelectedContentType] = useState<ContentTypeId | 'todas'>('todas');
  const [selectedPeriod, setSelectedPeriod] = useState<'todo' | 'hoy' | 'semana' | 'mes'>('todo');

  const filteredContent = selectedContentType === 'todas'
    ? topReactedContent
    : getContentByType(selectedContentType);

  const views = [
    { id: 'galeria' as const, label: 'Galería', icon: 'ri-emotion-line' },
    { id: 'contenido' as const, label: 'Contenido', icon: 'ri-article-line' },
    { id: 'metricas' as const, label: 'Métricas', icon: 'ri-bar-chart-line' },
    { id: 'score' as const, label: 'Score', icon: 'ri-flashlight-line' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-rose-500 via-pink-500 to-orange-500">
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/30"></div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-semibold mb-5">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
            SISTEMA DE REACCIONES RFW
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Más Allá del Simple <span className="text-yellow-300">Like</span>
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto text-sm md:text-base mb-8">
            8 reacciones con identidad propia. Expresá exactamente lo que sentís con un solo toque en posts, fotos, videos, comentarios, eventos, concursos, programas y locutores.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto">
            {[
              { label: 'Reacciones', value: reactionDashboardStats.totalReactions >= 1000000 ? (reactionDashboardStats.totalReactions / 1000000).toFixed(1) + 'M' : reactionDashboardStats.totalReactions.toLocaleString(), icon: 'ri-emotion-line' },
              { label: 'Hoy', value: reactionDashboardStats.reactionsToday.toLocaleString(), icon: 'ri-calendar-line' },
              { label: 'Esta Semana', value: reactionDashboardStats.reactionsThisWeek.toLocaleString(), icon: 'ri-calendar-check-line' },
              { label: 'Reactores Activos', value: (reactionDashboardStats.activeReactors / 1000).toFixed(0) + 'K', icon: 'ri-group-line' },
            ].map((stat, i) => (
              <div key={i} className="flex items-center gap-2 p-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10">
                <i className={`${stat.icon} text-white/70 text-lg`}></i>
                <div className="text-left">
                  <p className="text-lg font-bold text-white">{stat.value}</p>
                  <p className="text-[10px] text-white/60">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* View Switcher */}
      <div className="sticky top-0 z-30 bg-background-50/80 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center gap-1 overflow-x-auto no-scrollbar">
          {views.map((v) => (
            <button
              key={v.id}
              onClick={() => { setView(v.id); setExpandedReaction(null); }}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                view === v.id
                  ? 'bg-rose-500 text-white shadow-sm'
                  : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className={`${v.icon} text-[10px]`}></i>
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Galería View */}
        {view === 'galeria' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {reactionTypes.map((r) => (
                <ReactionDetailCard
                  key={r.id}
                  reaction={r}
                  expanded={expandedReaction === r.id}
                  onToggle={() => setExpandedReaction(expandedReaction === r.id ? null : r.id)}
                />
              ))}
            </div>

            {/* Aplicación: Dónde se usan las reacciones */}
            <div>
              <h3 className="text-lg font-bold text-foreground-950 mb-4">¿Dónde Aplican las Reacciones?</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {contentTypeConfigs.map((ct) => {
                  const applicable = getReactionsForContentType(ct.id);
                  return (
                    <div key={ct.id} className={`p-5 rounded-2xl ${ct.bgColor} border ${ct.bgColor === 'bg-sky-50' ? 'border-sky-200/50' : 'border-background-200/70'}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center">
                          <i className={`${ct.icon} ${ct.color} text-lg`}></i>
                        </div>
                        <div>
                          <h4 className={`text-sm font-bold ${ct.textColor}`}>{ct.name}</h4>
                          <p className="text-[9px] text-foreground-500">{applicable.length} reacciones</p>
                        </div>
                      </div>
                      <p className="text-[10px] text-foreground-500 mb-3 leading-relaxed">{ct.description}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {applicable.map((r) => (
                          <span key={r.id} className="w-7 h-7 rounded-full bg-white/70 flex items-center justify-center shadow-sm text-sm">
                            {r.emoji}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Contenido View */}
        {view === 'contenido' && (
          <div className="space-y-6">
            {/* Content Type Filters */}
            <div className="flex flex-wrap items-center gap-1.5">
              {[{ id: 'todas' as const, name: 'Todos', icon: 'ri-apps-line', color: 'text-foreground-600', bgColor: 'bg-background-200' }, ...contentTypeConfigs.map((ct) => ({ id: ct.id, name: ct.name, icon: ct.icon, color: ct.color, bgColor: ct.bgColor }))].map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setSelectedContentType(filter.id)}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    selectedContentType === filter.id
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${filter.icon} text-[9px]`}></i>
                  {filter.name}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              {filteredContent.map((item) => (
                <ContentListCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        )}

        {/* Métricas View */}
        {view === 'metricas' && (
          <div className="space-y-8">
            {/* Period Selector */}
            <div className="flex items-center gap-1.5">
              {[
                { id: 'todo' as const, label: 'Todo' },
                { id: 'hoy' as const, label: 'Hoy' },
                { id: 'semana' as const, label: 'Esta Semana' },
                { id: 'mes' as const, label: 'Este Mes' },
              ].map((p) => (
                <button
                  key={p.id}
                  onClick={() => setSelectedPeriod(p.id)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    selectedPeriod === p.id
                      ? 'bg-rose-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Dashboard Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total Reacciones', value: reactionDashboardStats.totalReactions.toLocaleString(), icon: 'ri-emotion-line', color: 'bg-rose-50 text-rose-600' },
                { label: 'Reacción Principal', value: reactionDashboardStats.mainReaction, sub: `#1 con ${reactionDashboardStats.mainReactionCount.toLocaleString()}`, icon: 'ri-thumb-up-line', color: 'bg-sky-50 text-sky-600' },
                { label: 'Tendencia', value: reactionDashboardStats.trendingReaction, sub: `Creciendo +${reactionDashboardStats.trendingReactionPercent}%`, icon: 'ri-fire-line', color: 'bg-orange-50 text-orange-600' },
                { label: 'Score Generado', value: reactionDashboardStats.totalScoreGenerated.toLocaleString(), sub: 'puntos RFW', icon: 'ri-flashlight-line', color: 'bg-amber-50 text-amber-600' },
              ].map((stat, i) => (
                <div key={i} className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                  <div className={`w-10 h-10 rounded-full ${stat.color} flex items-center justify-center mb-3`}>
                    <i className={`${stat.icon} text-base`}></i>
                  </div>
                  <p className="text-2xl font-bold text-foreground-950 mb-1">{stat.value}</p>
                  <p className="text-xs text-foreground-500">{stat.label}</p>
                  {stat.sub && <p className="text-[10px] text-foreground-400 mt-0.5">{stat.sub}</p>}
                </div>
              ))}
            </div>

            {/* Ranking de Reacciones */}
            <div>
              <h3 className="text-lg font-bold text-foreground-950 mb-4">Ranking de Reacciones</h3>
              <div className="space-y-2">
                {reactionTypes.map((r, i) => (
                  <div key={r.id} className="flex items-center gap-4 p-4 rounded-2xl bg-background-50 border border-background-200/70">
                    <span className="text-sm font-bold text-foreground-400 w-6">#{i + 1}</span>
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${r.gradient} flex items-center justify-center shadow-sm`}>
                      <span className="text-base">{r.emoji}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-bold ${r.textColor}`}>{r.name}</p>
                      <p className="text-[10px] text-foreground-500">{r.totalCount.toLocaleString()} · +{r.scoreValue} pts por reacción</p>
                    </div>
                    <div className="w-48 h-2 rounded-full bg-background-200 overflow-hidden">
                      <div
                        className={`h-full rounded-full bg-gradient-to-r ${r.gradient}`}
                        style={{ width: `${(r.totalCount / reactionTypes[0].totalCount) * 100}%` }}
                      ></div>
                    </div>
                    <div className="text-right shrink-0 w-16">
                      <span className={`text-xs font-semibold ${r.trend === 'up' ? 'text-emerald-600' : 'text-red-500'}`}>
                        {r.trend === 'up' ? '+' : '-'}{r.trendPercent}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Actividad Reciente */}
            <div>
              <h3 className="text-lg font-bold text-foreground-950 mb-4">Actividad Reciente</h3>
              <div className="space-y-2">
                {userReactions.map((ur) => {
                  const reaction = getReactionById(ur.reactionId);
                  if (!reaction) return null;
                  return (
                    <div key={ur.id} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
                      <img src={ur.userPhoto} alt={ur.userName} className="w-8 h-8 rounded-full object-cover object-top" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-foreground-950">
                          <span className="font-semibold">{ur.userName}</span>
                          {' '}reaccionó con{' '}
                          <span className="font-semibold">{reaction.emoji} {reaction.name}</span>
                          {' '}a un {getContentTypeById(ur.contentType)?.name?.toLowerCase()}
                        </p>
                      </div>
                      <span className="text-[10px] text-foreground-400 whitespace-nowrap">{ur.timeAgo}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Score View */}
        {view === 'score' && (
          <div className="space-y-8">
            {/* Score Overview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Score Total', value: reactionDashboardStats.totalScoreGenerated.toLocaleString(), icon: 'ri-flashlight-line', color: 'bg-amber-50 text-amber-600' },
                { label: 'Promedio por Usuario', value: reactionDashboardStats.avgReactionsPerUser.toString(), icon: 'ri-user-line', color: 'bg-sky-50 text-sky-600' },
                { label: 'Reacción Top Score', value: 'Está Brutal', sub: '+5 pts', icon: 'ri-fire-line', color: 'bg-orange-50 text-orange-600' },
                { label: 'Reactores Activos', value: (reactionDashboardStats.activeReactors / 1000).toFixed(0) + 'K', icon: 'ri-group-line', color: 'bg-emerald-50 text-emerald-600' },
              ].map((stat, i) => (
                <div key={i} className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                  <div className={`w-10 h-10 rounded-full ${stat.color} flex items-center justify-center mb-3`}>
                    <i className={`${stat.icon} text-base`}></i>
                  </div>
                  <p className="text-2xl font-bold text-foreground-950 mb-1">{stat.value}</p>
                  <p className="text-xs text-foreground-500">{stat.label}</p>
                  {stat.sub && <p className="text-[10px] text-foreground-400 mt-0.5">{stat.sub}</p>}
                </div>
              ))}
            </div>

            {/* Score por Reacción */}
            <div>
              <h3 className="text-lg font-bold text-foreground-950 mb-4">Score por Tipo de Reacción</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {reactionTypes.map((r) => (
                  <div key={r.id} className="p-4 rounded-2xl bg-background-50 border border-background-200/70">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${r.gradient} flex items-center justify-center shadow-sm`}>
                        <span className="text-base">{r.emoji}</span>
                      </div>
                      <p className={`text-sm font-bold ${r.textColor}`}>{r.name}</p>
                    </div>
                    <p className="text-2xl font-bold text-amber-600">+{r.scoreValue}</p>
                    <p className="text-[10px] text-foreground-500">puntos por reacción</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Score Actions */}
            <div>
              <h3 className="text-lg font-bold text-foreground-950 mb-4">Acciones que Generan Score</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {scoreActions.map((sa) => (
                  <div key={sa.id} className="flex items-center gap-4 p-4 rounded-2xl bg-background-50 border border-background-200/70">
                    <div className={`w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center shrink-0`}>
                      <i className={`${sa.icon} ${sa.color} text-lg`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-foreground-950">{sa.label}</p>
                      <p className="text-[10px] text-foreground-500">{sa.description}</p>
                    </div>
                    <span className="text-lg font-bold text-amber-600 shrink-0">+{sa.points}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Cross Links */}
        <div className="mt-12 pt-8 border-t border-background-200/70">
          <p className="text-xs font-semibold text-foreground-400 mb-4 text-center">EXPLORÁ TAMBIÉN</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { to: '/rankings', label: 'Rankings', icon: 'ri-bar-chart-line' },
              { to: '/insignias', label: 'Insignias', icon: 'ri-award-line' },
              { to: '/gamificacion', label: 'Gamificación', icon: 'ri-gamepad-line' },
            ].map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-600 hover:text-foreground-950 hover:bg-background-200 text-xs font-medium transition-all duration-200 whitespace-nowrap"
              >
                <i className={`${link.icon} text-[10px]`}></i>
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Regla Final */}
        <div className="mt-8 p-6 rounded-2xl bg-rose-100/70 border border-rose-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-rose-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-hand-heart-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Una reacción debe requerir un solo toque.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            Sin menús, sin confirmaciones, sin pasos extra. Tocás el emoji y listo — la reacción queda registrada al instante. Simple, rápido y divertido. Así se mantiene la interacción fluida y natural en toda la plataforma RFW.
          </p>
        </div>
      </div>
    </div>
  );
}